import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLang, pick } from "@/lib/i18n";
import { fileUrl } from "@/lib/api";

export default function Lightbox({ photos, index, onClose, onIndex, srcOf = fileUrl }) {
  const { lang } = useLang();
  const startX = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const p = photos[index];
  const go = useCallback((d) => { setLoaded(false); onIndex((index + d + photos.length) % photos.length); }, [index, photos.length, onIndex]);

  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", key);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", key); document.body.style.overflow = ""; };
  }, [go, onClose]);

  const onEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    startX.current = null;
  };
  const caption = pick(p, "caption", lang);
  const btn = "absolute top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/60 p-3 text-white backdrop-blur transition-colors hover:bg-red-600";

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black/95" data-testid="gallery-lightbox"
      onTouchStart={(e) => { startX.current = e.touches[0].clientX; }} onTouchEnd={onEnd}>
      <div className="flex items-center justify-between px-4 py-3">
        <span className="font-mono text-xs text-slate-400" data-testid="lightbox-counter">{index + 1} / {photos.length}</span>
        <button onClick={onClose} className="rounded-full border border-white/20 p-2 text-white" data-testid="gallery-lightbox-close" aria-label="Close"><X size={18} /></button>
      </div>
      <div className="relative flex flex-1 items-center justify-center px-2 pb-4 sm:px-16" onClick={(e) => e.target === e.currentTarget && onClose()}>
        {!loaded && <img src={srcOf(p.thumb_path || p.storage_path)} alt="" className="absolute max-h-full max-w-full object-contain opacity-60 blur-sm" />}
        <img key={p.id} src={srcOf(p.storage_path)} alt={p[`alt_${lang}`] || caption || "RECORDING ROOM"} onLoad={() => setLoaded(true)}
          className="relative max-h-[calc(100vh-130px)] max-w-full rounded-lg object-contain" data-testid="lightbox-image" />
        {photos.length > 1 && <>
          <button onClick={() => go(-1)} className={`${btn} left-2 sm:left-4`} data-testid="lightbox-prev" aria-label="Previous"><ChevronLeft size={20} /></button>
          <button onClick={() => go(1)} className={`${btn} right-2 sm:right-4`} data-testid="lightbox-next" aria-label="Next"><ChevronRight size={20} /></button>
        </>}
      </div>
      {caption && <p className="px-4 pb-5 text-center text-sm text-slate-300" data-testid="lightbox-caption">{caption}</p>}
    </div>
  );
}
